'use client';

/**
 * ProductDetails - Product entity detail view
 *
 * @deprecated Use EntityDetailsLoader with DetailsPanel instead.
 * This component is kept for fallback during migration.
 * Remove after schema-driven rendering is verified.
 *
 * Displays product information when a product node is selected.
 * Fetches its own data via useProductExplore hook.
 *
 * @component_role Product Detail View
 * @business_rule Shows product image, pricing, availability, dimensions,
 *   documents, and vendor/collection context. Links to Odoo product form.
 * @user_outcome Quick product inspection - see pricing, stock, specs without
 *   leaving the graph view.
 * @integration_point klyde_edge /neo/graph/explore/product
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useState } from 'react';
import { cn } from '@/lib/utils/cn';
import {
  Factory,
  Box,
  FileText,
  ExternalLink,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useProductExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useIntentStore } from '@/lib/stores/intent-store';
import type { IntentAxisScore } from '@/lib/api/types';
import { AxisBreakdownSection } from './sections/axis-breakdown-section';

interface ProductDetailsProps {
  nodeId: string;
}

export function ProductDetails({ nodeId }: ProductDetailsProps) {
  const productExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'product_explore'));
  const { data, isLoading } = useProductExplore(productExploreStrategy!, { product_id: nodeId });
  const product = data?.product;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Per-search axis breakdown. Two sources, in order:
  //   1. selectedNode.properties — the grid-widget stashes the full
  //      IntentGridProduct here on click, so the breakdown matches
  //      exactly what the user clicked.
  //   2. gridResponse scan — fallback for selections that arrive via
  //      graph navigation rather than a direct lane click.
  // Never persisted; recomputed every search + refine.
  const selectedProps = useGraphSelectionStore((s) => s.selectedNode?.properties ?? null);
  const gridLookup = useIntentStore((s) => {
    if (!s.gridResponse) return null;
    for (const line of s.gridResponse.lines) {
      const hit = line.products.find((p) => p.semantic_id === nodeId);
      if (hit) return { lineQuery: line.query, axes: hit.axis_breakdown ?? [] };
    }
    return null;
  });
  const axisBreakdown: IntentAxisScore[] =
    (selectedProps?.axis_breakdown as IntentAxisScore[] | undefined) ??
    gridLookup?.axes ??
    [];
  const axisLineQuery: string | null =
    (selectedProps?.line_query as string | undefined) ??
    gridLookup?.lineQuery ??
    null;

  // Build gallery from gallery_urls or fallback to primary
  const galleryImages = product?.images.gallery_urls?.length
    ? product.images.gallery_urls
    : product?.images.primary
      ? [product.images.primary]
      : [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading product...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-sm text-muted-foreground">
        Product details not available
      </div>
    );
  }

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  return (
    <div className="space-y-4">
      {/* Product Image Gallery */}
      {galleryImages.length > 0 && (
        <div className="space-y-2">
          {/* Main Image */}
          <div className="relative aspect-square bg-muted rounded overflow-hidden">
            <img
              src={galleryImages[selectedImageIndex]}
              alt={`${product.name} - Image ${selectedImageIndex + 1}`}
              className="w-full h-full object-contain"
            />
            {/* Navigation arrows for multiple images */}
            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImageIndex((i) => (i === 0 ? galleryImages.length - 1 : i - 1))}
                  className="absolute left-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-background/80 hover:bg-background transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setSelectedImageIndex((i) => (i === galleryImages.length - 1 ? 0 : i + 1))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-background/80 hover:bg-background transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                {/* Image counter */}
                <div className="absolute bottom-1 right-1 px-2 py-0.5 rounded bg-background/80 text-xs">
                  {selectedImageIndex + 1} / {galleryImages.length}
                </div>
              </>
            )}
          </div>
          {/* Thumbnail strip for multiple images */}
          {galleryImages.length > 1 && (
            <div className="flex gap-1 overflow-x-auto pb-1">
              {galleryImages.map((url, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={cn(
                    "flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-colors",
                    idx === selectedImageIndex
                      ? "border-primary"
                      : "border-transparent hover:border-muted-foreground/50"
                  )}
                >
                  <img
                    src={url}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Match breakdown — only present when product is on the current grid */}
      {axisBreakdown.length > 0 && (
        <AxisBreakdownSection axes={axisBreakdown} lineQuery={axisLineQuery} />
      )}

      {/* Price & Availability Summary */}
      <div className="grid grid-cols-2 gap-2">
        {product.list_price && (
          <div className="p-2 rounded bg-card border border-border text-center">
            <div className="text-lg font-semibold text-success">
              ${product.list_price.toFixed(0)}
            </div>
            <div className="text-xs text-muted-foreground">Price</div>
          </div>
        )}
        <div className="p-2 rounded bg-card border border-border text-center">
          <div className={cn(
            "text-lg font-semibold",
            (product.qty_available || 0) > 0 ? "text-success" : "text-destructive"
          )}>
            {product.qty_available ?? 0}
          </div>
          <div className="text-xs text-muted-foreground">In Stock</div>
        </div>
      </div>

      {/* Source Info (Vendor & Collection) */}
      {(product.vendor_name || product.collection_name) && (
        <div className="p-2 rounded bg-accent/50 border border-border">
          {product.vendor_name && (
            <div className="flex items-center gap-2 text-sm">
              <Factory className="h-4 w-4 text-muted-foreground" />
              <span>{product.vendor_name}</span>
            </div>
          )}
          {product.collection_name && (
            <div className="flex items-center gap-2 text-sm mt-1">
              <Box className="h-4 w-4 text-muted-foreground" />
              <span>{product.collection_name}</span>
            </div>
          )}
        </div>
      )}

      {/* Details */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">DETAILS</h4>
        <div className="space-y-1 text-sm">
          {product.sku && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">SKU</span>
              <span className="font-mono text-xs">{product.sku}</span>
            </div>
          )}
          {product.lead_time && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Lead Time</span>
              <span>{product.lead_time}</span>
            </div>
          )}
          {(product.width_mm || product.height_mm || product.depth_mm) && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Dimensions</span>
              <span className="text-xs">
                {[
                  product.width_mm && `W${product.width_mm}`,
                  product.depth_mm && `D${product.depth_mm}`,
                  product.height_mm && `H${product.height_mm}`,
                ].filter(Boolean).join(' × ')}mm
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Documents */}
      {product.documents.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            DOCUMENTS ({product.documents.length})
          </h4>
          <div className="space-y-1">
            {product.documents.slice(0, 3).map((doc, idx) => (
              <a
                key={idx}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-2 rounded bg-card border border-border hover:bg-accent transition-colors text-sm"
              >
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="truncate flex-1">{doc.name}</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Sales Description - customer-facing */}
      {product.description_sale && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-1">SALES DESCRIPTION</h4>
          <p className="text-sm line-clamp-4">
            {product.description_sale}
          </p>
        </div>
      )}

      {/* Technical Description */}
      {product.description && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-1">DESCRIPTION</h4>
          <p className="text-sm text-muted-foreground line-clamp-3">
            {product.description}
          </p>
        </div>
      )}

      {/* Open in Odoo */}
      {product.odoo_id && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">ACTIONS</h4>
          <a
            href={`${ODOO_HOST}/web#id=${product.odoo_id}&model=product.template&view_type=form`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-accent transition-colors text-sm"
          >
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{product.name || 'Product'}</span>
          </a>
        </div>
      )}
    </div>
  );
}
